import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { ORDER_DETAILS_QUERY } from './operations';
import { ERROR_MESSAGE } from '../Messages/Messages';
import {
  AccountBlock,
  SectionHeading,
  TmntText,
  Loading,
} from '../Common';
import OrderDetailsContent from './OrderDetailsContent';
import InvalidOrderErrorPage from './InvalidOrderErrorPage';
import RnEOrderDetailsContent from './ReturnExchangeOrderDetailsBlock/RnEOrderDetailsContent';
import PaymentStatusBanner from './PaymentStatusBanner';
import { useOrderDetailsAnalytics } from '../../hooks/useOrderDetailsAnalytics';
import { decodeHtmlEntities } from '../../tools/html/decode';

export default function OrderDetailsBlock() {
  const [variables, setVariables] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null);
  const [successBanner, setSuccessBanner] = useState(null);

  // Analytics tracking for Order Details MFE experience
  const { triggerOrderDetailsView, isReady: isAnalyticsReady } = useOrderDetailsAnalytics();

  useEffect(() => {
    // Normalize malformed URLs possibly containing HTML entities or params in hash
    const currentHref = decodeHtmlEntities(window.location.href);
    const url = new URL(currentHref);
    const searchParams = new URLSearchParams(url.search || '');

    // Try to recover dropped params into the hash
    if (!searchParams.get('orderNumber') && url.hash) {
      const hash = decodeHtmlEntities(url.hash.startsWith('#') ? url.hash.slice(1) : url.hash);
      const hashParams = new URLSearchParams(hash.replace(/^\?/, ''));
      const hashOrder = hashParams.get('orderNumber') || hashParams.get('ordernumber');
      const hashEmail = hashParams.get('orderEmail') || hashParams.get('orderemail');
      const hashChannel = hashParams.get('channel');
      const hashPlacedTime = hashParams.get('placedTime');
      if (hashOrder) searchParams.set('orderNumber', hashOrder);
      if (hashEmail) searchParams.set('orderEmail', hashEmail);
      if (hashChannel) searchParams.set('channel', hashChannel);
      if (hashPlacedTime) searchParams.set('placedTime', hashPlacedTime);
    }

    let orderId = searchParams.get('orderNumber');
    let email = searchParams.get('orderEmail');
    let channel = searchParams.get('channel');
    let placedTime = searchParams.get('placedTime');

    // If params are missing, try to restore from sessionStorage
    if (!orderId) {
      try {
        const savedRaw = window.sessionStorage.getItem('orderDetailsParams');
        if (savedRaw) {
          const saved = JSON.parse(savedRaw);
          orderId = saved?.orderNumber || saved?.orderId || orderId;
          email = saved?.orderEmail || saved?.email || email;
          channel = saved?.channel || channel;
          placedTime = saved?.placedTime || placedTime;

          if (orderId) {
            // Update URL to reflect restored params without reloading
            const newParams = new URLSearchParams(url.search);
            newParams.set('orderNumber', orderId);
            if (channel === 'store' && placedTime) {
              newParams.set('channel', 'store');
              newParams.set('placedTime', placedTime);
              newParams.delete('orderEmail');
            } else if (email) {
              newParams.set('orderEmail', email);
              newParams.delete('channel');
              newParams.delete('placedTime');
            }
            const newUrl = `${url.pathname}?${newParams.toString()}`;
            window.history.replaceState(null, '', newUrl);
          }
        }
      } catch {
        // no-op
      }
    }

    if (!orderId) return;

    // Persist current params for future restores (e.g., on language switch)
    try {
      const payload = {
        orderNumber: orderId, orderEmail: email, channel, placedTime,
      };
      window.sessionStorage.setItem('orderDetailsParams', JSON.stringify(payload));
    } catch {
      // no-op
    }

    const vars = channel === 'store' && placedTime
      ? { orderId, channel: 'store', placedTime }
      : { orderId, email };

    setVariables(vars);
  }, []);

  const { loading, error, data } = useQuery(ORDER_DETAILS_QUERY, {
    variables,
    context: { batch: true },
    ssr: false,
    skip: !variables || !variables.orderId,
  });

  useEffect(() => {
    // Trigger analytics when order details are successfully loaded
    if (!loading && data?.orderDetails?.success && isAnalyticsReady) {
      triggerOrderDetailsView();
    }
  }, [loading, data, isAnalyticsReady, triggerOrderDetailsView]);

  // Hide DRE loading indicator immediately when component mounts
  useEffect(() => {
    const hideDRELoadingIndicator = () => {
      const selectors = [
        '[data-testid="dre-spinner-overlay"]',
        '.dre-spinner-overlay',
        '#dre-mfe .dre-spinner-overlay',
      ];

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          // eslint-disable-next-line no-param-reassign
          element.style.display = 'none';
        });
      });
    };

    hideDRELoadingIndicator();

    // Check periodically for the first few seconds
    const intervalId = setInterval(hideDRELoadingIndicator, 100);
    setTimeout(() => clearInterval(intervalId), 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Hide DRE loading indicator if there's an error
  useEffect(() => {
    if (error) {
      const selectors = [
        '[data-testid="dre-spinner-overlay"]',
        '.dre-spinner-overlay',
        '#dre-mfe .dre-spinner-overlay',
      ];

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          // eslint-disable-next-line no-param-reassign
          element.style.display = 'none';
        });
      });
    }
  }, [error]);

  if (error) return ERROR_MESSAGE;

  const { orderDetails = {} } = data || {};
  const { success, errors, orderDetailItems = {} } = orderDetails;
  const {
    heading, status, isEmpty, isRnEOrder, wssPaymentDetails,
  } = orderDetailItems || {};

  // Check if we should show Payment Information header instead of order details
  const showPaymentHeader = wssPaymentDetails && wssPaymentDetails.paymentUpdateNeeded;

  const hasErrors = errors?.length > 0;
  const invalidOrderError = errors?.find(
    (err) => err.message?.key === 'ERR_ENTEREDINVALIDORDERNUMBER',
  );

  if (!success && hasErrors) {
    if (invalidOrderError) {
      return (
        <InvalidOrderErrorPage
          title={data?.textFor?.invalidOrderHeader}
          content={data?.textFor?.errorInvalidOrderContent}
        />
      );
    }
    return ERROR_MESSAGE;
  }

  const computedBannerMessage = successBanner || errorBanner || wssPaymentDetails?.initialErrorBannerMessage || { key: 'WSS_PAYMENT_GENERAL_ERROR_PLACEHOLDER', value: 'There was an error processing your payment' };

  return (
    <Loading isLoading={loading}>
      {!loading && (
        <div className="order-details-block" data-testid="order-details-block">
          <AccountBlock
            heading={(
              <div className="heading">
                <div className="section-header-container">
                  <SectionHeading>
                    <span className="h3" data-testid="order-details-block-heading">
                      {showPaymentHeader ? (
                        <TmntText tmnt={wssPaymentDetails?.paymentInformationHeaderText} />
                      ) : (
                        <TmntText tmnt={heading} />
                      )}
                    </span>
                  </SectionHeading>
                </div>
                {showPaymentHeader && <div className="section-divider" />}
              </div>
            )}
            rightHeading={
              !isEmpty && !showPaymentHeader ? (
                <SectionHeading alignedRight icon={status?.value}>
                  <span className="h3" data-testid="order-details-block-status">
                    <TmntText tmnt={status?.label} />
                  </span>
                </SectionHeading>
              ) : null
            }
            banner={showPaymentHeader ? (
              <PaymentStatusBanner
                message={computedBannerMessage}
                type={successBanner ? 'success' : 'error'}
              />
            ) : null}
          >
            {isRnEOrder ? (
              <RnEOrderDetailsContent rneQueryData={data} />
            ) : (
              <OrderDetailsContent
                queryData={data}
                onPaymentError={setErrorBanner}
                onPaymentSuccess={(successMessage) => {
                  setErrorBanner(null);
                  setSuccessBanner(successMessage || { key: 'WSS_PAYMENT_SUCCESS', value: 'Payment updated successfully' });
                }}
              />
            )}
          </AccountBlock>
        </div>
      )}
    </Loading>
  );
}
